import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Skeleton,
  FormModal,
  ConfirmModal,
  Input,
  Select,
  TextArea,
} from '../../components/ui';
import { adminAPI } from '../../services/adminApi';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  FitnessCenter,
  Add,
  Edit,
  Delete,
  Search,
  Layers,
  Check,
  Scale,
  Repeat,
  ViewList,
  ExpandMore,
  ExpandLess,
  ContentCopy,
  CheckBox,
  CheckBoxOutlineBlank,
  Link as LinkIcon,
  LinkOff
} from '../../mui-icons';
import './ActivitiesPage.css';

// Grupos musculares para o dropdown
const MUSCLE_GROUPS = [
  'Peito', 'Costas', 'Ombros', 'Bíceps', 'Tríceps',
  'Antebraço', 'Abdômen', 'Quadríceps', 'Posterior',
  'Glúteos', 'Panturrilha', 'Core'
];

const ActivitiesPage = () => {
  const { canEdit } = useAdminAuth();

  // ==================== STATE ====================

  // Atividades
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activitiesSearch, setActivitiesSearch] = useState('');

  // Divisões
  const [divisions, setDivisions] = useState([]);
  const [divisionsLoading, setDivisionsLoading] = useState(false);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [divisionsSearch, setDivisionsSearch] = useState('');

  // Exercícios (vinculados e disponíveis)
  const [assignedExercises, setAssignedExercises] = useState([]);
  const [unassignedExercises, setUnassignedExercises] = useState([]);
  const [exercisesLoading, setExercisesLoading] = useState(false);
  const [exercisesSearch, setExercisesSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('');

  // Seleções para ações em massa
  const [selectedToAssign, setSelectedToAssign] = useState([]);
  const [selectedToUnassign, setSelectedToUnassign] = useState([]);

  // Modais
  const [activityModal, setActivityModal] = useState({ open: false, mode: 'create', data: null });
  const [divisionModal, setDivisionModal] = useState({ open: false, mode: 'create', data: null });
  const [exerciseModal, setExerciseModal] = useState({ open: false, mode: 'create', data: null });
  const [copyModal, setCopyModal] = useState({ open: false, exerciseIds: [] });
  const [confirmModal, setConfirmModal] = useState({ open: false, action: null, message: '', onConfirm: null });

  // Loading de ações
  const [actionLoading, setActionLoading] = useState(false);

  // Accordion (mobile)
  const [expandedSection, setExpandedSection] = useState('activities');

  // ==================== LOAD DATA ====================

  // Carregar atividades
  const loadActivities = useCallback(async () => {
    setActivitiesLoading(true);
    try {
      const response = await adminAPI.getActivities();
      if (response.status === 'success') {
        setActivities(response.data || []);
      }
    } catch (err) {
      console.error('Erro ao carregar atividades:', err);
    } finally {
      setActivitiesLoading(false);
    }
  }, []);

  // Carregar divisões de uma atividade
  const loadDivisions = useCallback(async (activityId) => {
    setDivisionsLoading(true);
    setDivisions([]);
    setSelectedDivision(null);
    setAssignedExercises([]);
    setUnassignedExercises([]);
    
    try {
      const response = await adminAPI.getDivisions(activityId);
      if (response.status === 'success') {
        setDivisions(response.data || []);
      }
    } catch (err) {
      console.error('Erro ao carregar divisões:', err);
    } finally {
      setDivisionsLoading(false);
    }
  }, []);

  // Carregar status de exercícios (vinculados/disponíveis)
  const loadExercisesBulkStatus = useCallback(async (activityId, divisionId) => {
    setExercisesLoading(true);
    setAssignedExercises([]);
    setUnassignedExercises([]);
    setSelectedToAssign([]);
    setSelectedToUnassign([]);

    try {
      const response = await adminAPI.getExercisesBulkStatus(activityId, divisionId);
      if (response.status === 'success') {
        // API retorna assigned_exercises e unassigned_exercises
        setAssignedExercises(response.data.assigned_exercises || response.data.assigned || []);
        setUnassignedExercises(response.data.unassigned_exercises || response.data.unassigned || []);
      }
    } catch (err) {
      console.error('Erro ao carregar exercícios:', err);
    } finally {
      setExercisesLoading(false);
    }
  }, []);

  // Carregar dados iniciais
  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  // ==================== SELECTION HANDLERS ====================

  const handleSelectActivity = (activity) => {
    setSelectedActivity(activity);
    setSelectedDivision(null);
    setExpandedSection('divisions');
    loadDivisions(activity.idactivities);
  };

  const handleSelectDivision = (division) => {
    setSelectedDivision(division);
    setExpandedSection('exercises');
    loadExercisesBulkStatus(selectedActivity.idactivities, division.idactivitiesdivisions);
  };

  // ==================== FILTERED LISTS ====================

  const filteredActivities = useMemo(() => {
    if (!activitiesSearch) return activities;
    return activities.filter(a => 
      a.activity.toLowerCase().includes(activitiesSearch.toLowerCase())
    );
  }, [activities, activitiesSearch]);

  const filteredDivisions = useMemo(() => {
    if (!divisionsSearch) return divisions;
    return divisions.filter(d => 
      d.division.toLowerCase().includes(divisionsSearch.toLowerCase())
    );
  }, [divisions, divisionsSearch]);

  const filteredAssigned = useMemo(() => {
    let list = assignedExercises;
    if (exercisesSearch) {
      list = list.filter(e => e.exercise.toLowerCase().includes(exercisesSearch.toLowerCase()));
    }
    if (groupFilter) {
      list = list.filter(e => e.group === groupFilter);
    }
    return list;
  }, [assignedExercises, exercisesSearch, groupFilter]);

  const filteredUnassigned = useMemo(() => {
    let list = unassignedExercises;
    if (exercisesSearch) {
      list = list.filter(e => e.exercise.toLowerCase().includes(exercisesSearch.toLowerCase()));
    }
    if (groupFilter) {
      list = list.filter(e => e.group === groupFilter);
    }
    return list;
  }, [unassignedExercises, exercisesSearch, groupFilter]);

  // Extrair grupos únicos dos exercícios
  const availableGroups = useMemo(() => {
    const allExercises = [...assignedExercises, ...unassignedExercises];
    return [...new Set(allExercises.map(e => e.group).filter(Boolean))].sort();
  }, [assignedExercises, unassignedExercises]);

  // ==================== ACTIVITY ACTIONS ====================

  const handleSaveActivity = async (formData) => {
    setActionLoading(true);
    try {
      console.log('=== DEBUG handleSaveActivity ===');
      console.log('Mode:', activityModal.mode);
      console.log('Activity ID:', activityModal.data?.idactivities);
      console.log('FormData:', JSON.stringify(formData, null, 2));
      
      if (activityModal.mode === 'create') {
        const response = await adminAPI.createActivity(formData);
        console.log('Create Response:', response);
      } else {
        const response = await adminAPI.updateActivity(activityModal.data.idactivities, formData);
        console.log('Update Response:', response);
      }
      setActivityModal({ open: false, mode: 'create', data: null });
      loadActivities();
    } catch (err) {
      console.error('=== ERRO handleSaveActivity ===', err);
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteActivity = async (activity) => {
    setConfirmModal({
      open: true,
      action: 'delete-activity',
      message: `Excluir a atividade "${activity.activity}"? Isso removerá todas as divisões e exercícios associados.`,
      onConfirm: async () => {
        setActionLoading(true);
        try {
          await adminAPI.deleteActivity(activity.idactivities);
          if (selectedActivity?.idactivities === activity.idactivities) {
            setSelectedActivity(null);
            setDivisions([]);
            setSelectedDivision(null);
          }
          loadActivities();
        } catch (err) {
          alert(err.message);
        } finally {
          setActionLoading(false);
          setConfirmModal({ open: false, action: null, message: '', onConfirm: null });
        }
      }
    });
  };

  // ==================== DIVISION ACTIONS ====================

  const handleSaveDivision = async (formData) => {
    setActionLoading(true);
    try {
      console.log('=== DEBUG handleSaveDivision ===');
      console.log('Mode:', divisionModal.mode);
      console.log('Activity ID:', selectedActivity.idactivities);
      console.log('Division ID:', divisionModal.data?.idactivitiesdivisions);
      console.log('FormData:', JSON.stringify(formData, null, 2));
      
      if (divisionModal.mode === 'create') {
        const response = await adminAPI.createDivision(selectedActivity.idactivities, formData);
        console.log('Create Response:', response);
      } else {
        const response = await adminAPI.updateDivision(
          selectedActivity.idactivities,
          divisionModal.data.idactivitiesdivisions,
          formData
        );
        console.log('Update Response:', response);
      }
      setDivisionModal({ open: false, mode: 'create', data: null });
      loadDivisions(selectedActivity.idactivities);
    } catch (err) {
      console.error('=== ERRO handleSaveDivision ===', err);
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteDivision = async (division) => {
    setConfirmModal({
      open: true,
      action: 'delete-division',
      message: `Excluir a divisão "${division.division}"? Isso removerá todos os vínculos de exercícios.`,
      onConfirm: async () => {
        setActionLoading(true);
        try {
          await adminAPI.deleteDivision(selectedActivity.idactivities, division.idactivitiesdivisions);
          if (selectedDivision?.idactivitiesdivisions === division.idactivitiesdivisions) {
            setSelectedDivision(null);
            setAssignedExercises([]);
            setUnassignedExercises([]);
          }
          loadDivisions(selectedActivity.idactivities);
        } catch (err) {
          alert(err.message);
        } finally {
          setActionLoading(false);
          setConfirmModal({ open: false, action: null, message: '', onConfirm: null });
        }
      }
    });
  };

  // ==================== EXERCISE ACTIONS ====================

  const handleSaveExercise = async (formData) => {
    setActionLoading(true);
    try {
      console.log('=== DEBUG handleSaveExercise ===');
      console.log('Mode:', exerciseModal.mode);
      console.log('Activity ID:', selectedActivity.idactivities);
      console.log('Division ID:', selectedDivision.idactivitiesdivisions);
      console.log('Exercise ID:', exerciseModal.data?.idactivitiesdivisionsexercises);
      console.log('FormData:', JSON.stringify(formData, null, 2));

      if (exerciseModal.mode === 'create') {
        // NÃO incluir idactivitiesdivisions - já está na URL
        const response = await adminAPI.createExercise(
          selectedActivity.idactivities,
          selectedDivision.idactivitiesdivisions,
          formData
        );
        console.log('Create Response:', response);
      } else {
        const response = await adminAPI.updateExercise(
          selectedActivity.idactivities,
          selectedDivision.idactivitiesdivisions,
          exerciseModal.data.idactivitiesdivisionsexercises,
          formData
        );
        console.log('Update Response:', response);
      }
      setExerciseModal({ open: false, mode: 'create', data: null });
      loadExercisesBulkStatus(selectedActivity.idactivities, selectedDivision.idactivitiesdivisions);
    } catch (err) {
      console.error('=== ERRO handleSaveExercise ===', err);
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteExercise = async (exercise) => {
    setConfirmModal({
      open: true,
      action: 'delete-exercise',
      message: `Excluir o exercício "${exercise.exercise}" permanentemente?`,
      onConfirm: async () => {
        setActionLoading(true);
        try {
          await adminAPI.deleteExercise(
            selectedActivity.idactivities,
            selectedDivision.idactivitiesdivisions,
            exercise.idactivitiesdivisionsexercises
          );
          loadExercisesBulkStatus(selectedActivity.idactivities, selectedDivision.idactivitiesdivisions);
        } catch (err) {
          alert(err.message);
        } finally {
          setActionLoading(false);
          setConfirmModal({ open: false, action: null, message: '', onConfirm: null });
        }
      }
    });
  };

  // ==================== BULK ACTIONS ====================

  const handleBulkAssign = () => {
    if (selectedToAssign.length === 0) return;
    
    setConfirmModal({
      open: true,
      action: 'bulk-assign',
      message: `Vincular ${selectedToAssign.length} exercício(s) à divisão "${selectedDivision.division}"?`,
      onConfirm: async () => {
        setActionLoading(true);
        try {
          console.log('=== DEBUG handleBulkAssign ===');
          console.log('Activity ID:', selectedActivity.idactivities);
          console.log('Division ID:', selectedDivision.idactivitiesdivisions);
          console.log('Exercise IDs:', selectedToAssign);
          console.log('Request Body:', JSON.stringify({ exercise_ids: selectedToAssign }, null, 2));
          
          const response = await adminAPI.bulkAssignExercises(
            selectedActivity.idactivities,
            selectedDivision.idactivitiesdivisions,
            selectedToAssign
          );
          console.log('Response:', response);
          setSelectedToAssign([]);
          loadExercisesBulkStatus(selectedActivity.idactivities, selectedDivision.idactivitiesdivisions);
        } catch (err) {
          console.error('=== ERRO handleBulkAssign ===', err);
          alert(err.message);
        } finally {
          setActionLoading(false);
          setConfirmModal({ open: false, action: null, message: '', onConfirm: null });
        }
      }
    });
  };

  const handleBulkUnassign = () => {
    if (selectedToUnassign.length === 0) return;
    
    setConfirmModal({
      open: true,
      action: 'bulk-unassign',
      message: `Desvincular ${selectedToUnassign.length} exercício(s) da divisão "${selectedDivision.division}"?`,
      onConfirm: async () => {
        setActionLoading(true);
        try {
          console.log('=== DEBUG handleBulkUnassign ===');
          console.log('Activity ID:', selectedActivity.idactivities);
          console.log('Division ID:', selectedDivision.idactivitiesdivisions);
          console.log('Exercise IDs:', selectedToUnassign);
          console.log('Request Body:', JSON.stringify({ exercise_ids: selectedToUnassign }, null, 2));
          
          const response = await adminAPI.bulkDeleteExercises(
            selectedActivity.idactivities,
            selectedDivision.idactivitiesdivisions,
            selectedToUnassign
          );
          console.log('Response:', response);
          setSelectedToUnassign([]);
          loadExercisesBulkStatus(selectedActivity.idactivities, selectedDivision.idactivitiesdivisions);
        } catch (err) {
          console.error('=== ERRO handleBulkUnassign ===', err);
          alert(err.message);
        } finally {
          setActionLoading(false);
          setConfirmModal({ open: false, action: null, message: '', onConfirm: null });
        }
      }
    });
  };

  const handleBulkCopy = async (targetDivisionId) => {
    setActionLoading(true);
    try {
      await adminAPI.bulkCopyExercises(
        selectedActivity.idactivities,
        targetDivisionId,
        selectedDivision.idactivitiesdivisions,
        copyModal.exerciseIds
      );
      setCopyModal({ open: false, exerciseIds: [] });
      alert('Exercícios copiados com sucesso!');
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle checkbox helpers
  const toggleAssignSelection = (id) => {
    setSelectedToAssign(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleUnassignSelection = (id) => {
    setSelectedToUnassign(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Select all / Deselect all helpers
  const selectAllToAssign = () => {
    const ids = filteredUnassigned.map(e => e.idactivitiesdivisionsexercises);
    setSelectedToAssign(ids);
  };

  const deselectAllToAssign = () => {
    setSelectedToAssign([]);
  };

  const selectAllToUnassign = () => {
    const ids = filteredAssigned.map(e => e.idactivitiesdivisionsexercises);
    setSelectedToUnassign(ids);
  };

  const deselectAllToUnassign = () => {
    setSelectedToUnassign([]);
  };

  // ==================== RENDER ====================

  return (
    <div className="activities-page-v2">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <FitnessCenter sx={{ fontSize: 32 }} />
          <div>
            <h1>Atividades, Divisões e Exercícios</h1>
            <p>Gerenciamento completo do catálogo de treinos</p>
          </div>
        </div>
      </div>

      {/* Three Column Layout */}
      <div className="three-columns-container">
        {/* Column 1: Atividades */}
        <div className={`column column-activities ${expandedSection === 'activities' ? 'expanded' : ''}`}>
          <div 
            className="column-header"
            onClick={() => setExpandedSection(expandedSection === 'activities' ? '' : 'activities')}
          >
            <h3>
              <FitnessCenter sx={{ fontSize: 20 }} />
              Atividades
              {selectedActivity && <span className="selected-badge">{selectedActivity.activity}</span>}
            </h3>
            <span className="expand-icon">
              {expandedSection === 'activities' ? <ExpandLess /> : <ExpandMore />}
            </span>
          </div>

          <div className="column-content pn-card pn-card--default">
            <div className="column-toolbar">
              <div className="search-box">
                <Search className="search-icon" />
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={activitiesSearch}
                  onChange={(e) => setActivitiesSearch(e.target.value)}
                />
              </div>
              {canEdit() && (
                <button 
                  className="add-btn"
                  onClick={() => setActivityModal({ open: true, mode: 'create', data: null })}
                >
                  <Add /> Nova
                </button>
              )}
            </div>

            <div className="column-list">
              {activitiesLoading ? (
                <>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} height={52} style={{ marginBottom: 8 }} />
                  ))}
                </>
              ) : filteredActivities.length === 0 ? (
                <div className="empty-state">
                  <FitnessCenter sx={{ fontSize: 40, opacity: 0.3 }} />
                  <p>Nenhuma atividade encontrada</p>
                  {canEdit() && (
                    <button 
                      className="empty-btn"
                      onClick={() => setActivityModal({ open: true, mode: 'create', data: null })}
                    >
                      <Add /> Criar primeira atividade
                    </button>
                  )}
                </div>
              ) : (
                filteredActivities.map(activity => (
                  <div 
                    key={activity.idactivities}
                    className={`list-item ${selectedActivity?.idactivities === activity.idactivities ? 'selected' : ''}`}
                    onClick={() => handleSelectActivity(activity)}
                  >
                    <div className="item-info">
                      <span className="item-name">{activity.activity}</span>
                      {activity.category && (
                        <span className="item-category">{activity.category}</span>
                      )}
                    </div>
                    {canEdit() && (
                      <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                        <button 
                          className="icon-btn"
                          onClick={() => setActivityModal({ open: true, mode: 'edit', data: activity })}
                        >
                          <Edit sx={{ fontSize: 16 }} />
                        </button>
                        <button 
                          className="icon-btn danger"
                          onClick={() => handleDeleteActivity(activity)}
                        >
                          <Delete sx={{ fontSize: 16 }} />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Column 2: Divisões */}
        <div className={`column column-divisions ${expandedSection === 'divisions' ? 'expanded' : ''}`}>
          <div 
            className="column-header"
            onClick={() => setExpandedSection(expandedSection === 'divisions' ? '' : 'divisions')}
          >
            <h3>
              <Layers sx={{ fontSize: 20 }} />
              Divisões
              {selectedDivision && <span className="selected-badge">{selectedDivision.division}</span>}
            </h3>
            <span className="expand-icon">
              {expandedSection === 'divisions' ? <ExpandLess /> : <ExpandMore />}
            </span>
          </div>

          <div className="column-content pn-card pn-card--default">
            {!selectedActivity ? (
              <div className="placeholder-state">
                <Layers sx={{ fontSize: 48, opacity: 0.3 }} />
                <p>Selecione uma atividade</p>
              </div>
            ) : (
              <>
                <div className="column-toolbar">
                  <div className="search-box">
                    <Search className="search-icon" />
                    <input
                      type="text"
                      placeholder="Buscar..."
                      value={divisionsSearch}
                      onChange={(e) => setDivisionsSearch(e.target.value)}
                    />
                  </div>
                  {canEdit() && (
                    <button 
                      className="add-btn"
                      onClick={() => setDivisionModal({ open: true, mode: 'create', data: null })}
                    >
                      <Add /> Nova
                    </button>
                  )}
                </div>

                <div className="column-list">
                  {divisionsLoading ? (
                    <>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} height={52} style={{ marginBottom: 8 }} />
                  ))}
                </>
                  ) : filteredDivisions.length === 0 ? (
                    <div className="empty-state">
                      <Layers sx={{ fontSize: 40, opacity: 0.3 }} />
                      <p>Nenhuma divisão encontrada</p>
                      {canEdit() && (
                        <button 
                          className="empty-btn"
                          onClick={() => setDivisionModal({ open: true, mode: 'create', data: null })}
                        >
                          <Add /> Criar primeira divisão
                        </button>
                      )}
                    </div>
                  ) : (
                    filteredDivisions.map(division => (
                      <div 
                        key={division.idactivitiesdivisions}
                        className={`list-item ${selectedDivision?.idactivitiesdivisions === division.idactivitiesdivisions ? 'selected' : ''}`}
                        onClick={() => handleSelectDivision(division)}
                      >
                        <div className="item-info">
                          <span className="item-name">{division.division}</span>
                          {division.description && (
                            <span className="item-desc">{division.description}</span>
                          )}
                        </div>
                        {canEdit() && (
                          <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                            <button 
                              className="icon-btn"
                              onClick={() => setDivisionModal({ open: true, mode: 'edit', data: division })}
                            >
                              <Edit sx={{ fontSize: 16 }} />
                            </button>
                            <button 
                              className="icon-btn danger"
                              onClick={() => handleDeleteDivision(division)}
                            >
                              <Delete sx={{ fontSize: 16 }} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Column 3: Exercícios */}
        <div className={`column column-exercises ${expandedSection === 'exercises' ? 'expanded' : ''}`}>
          <div 
            className="column-header"
            onClick={() => setExpandedSection(expandedSection === 'exercises' ? '' : 'exercises')}
          >
            <h3>
              <FitnessCenter sx={{ fontSize: 20 }} />
              Exercícios
            </h3>
            <span className="expand-icon">
              {expandedSection === 'exercises' ? <ExpandLess /> : <ExpandMore />}
            </span>
          </div>

          <div className="column-content pn-card pn-card--default">
            {!selectedDivision ? (
              <div className="placeholder-state">
                <FitnessCenter sx={{ fontSize: 48, opacity: 0.3 }} />
                <p>Selecione uma divisão</p>
              </div>
            ) : (
              <>
                <div className="column-toolbar exercises-toolbar">
                  <div className="toolbar-row">
                    <div className="search-box">
                      <Search className="search-icon" />
                      <input
                        type="text"
                        placeholder="Buscar..."
                        value={exercisesSearch}
                        onChange={(e) => setExercisesSearch(e.target.value)}
                      />
                    </div>
                    <select
                      className="group-filter"
                      value={groupFilter}
                      onChange={(e) => setGroupFilter(e.target.value)}
                    >
                      <option value="">Todos os grupos</option>
                      {availableGroups.map(group => (
                        <option key={group} value={group}>{group}</option>
                      ))}
                    </select>
                  </div>
                  {canEdit() && (
                    <button 
                      className="add-btn full"
                      onClick={() => setExerciseModal({ open: true, mode: 'create', data: null })}
                    >
                      <Add /> Novo Exercício
                    </button>
                  )}
                </div>

                <div className="column-list exercises-list">
                  {exercisesLoading ? (
                    <>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} height={52} style={{ marginBottom: 8 }} />
                  ))}
                </>
                  ) : (
                    <>
                      {/* Seção: Vinculados */}
                      <div className="exercises-section">
                        <div className="section-header assigned">
                          <CheckBox sx={{ fontSize: 18 }} />
                          <span>Vinculados ({filteredAssigned.length})</span>
                          {canEdit() && filteredAssigned.length > 0 && (
                            <div className="select-all-btns">
                              <button 
                                className="select-btn"
                                onClick={selectAllToUnassign}
                                title="Selecionar todos"
                              >
                                Todos
                              </button>
                              <button 
                                className="select-btn"
                                onClick={deselectAllToUnassign}
                                title="Limpar seleção"
                              >
                                Nenhum
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {filteredAssigned.length === 0 ? (
                          <div className="section-empty">Nenhum exercício vinculado</div>
                        ) : (
                          filteredAssigned.map(exercise => (
                            <div 
                              key={exercise.idactivitiesdivisionsexercises} 
                              className={`exercise-item assigned ${canEdit() && selectedToUnassign.includes(exercise.idactivitiesdivisionsexercises) ? 'selected' : ''}`}
                              onClick={canEdit() ? () => toggleUnassignSelection(exercise.idactivitiesdivisionsexercises) : undefined}
                            >
                              {canEdit() && (
                                <span className="checkbox-indicator">
                                  {selectedToUnassign.includes(exercise.idactivitiesdivisionsexercises) 
                                    ? <CheckBox sx={{ fontSize: 20 }} /> 
                                    : <CheckBoxOutlineBlank sx={{ fontSize: 20 }} />
                                  }
                                </span>
                              )}
                              <div className="exercise-info">
                                <span className="exercise-name">{exercise.exercise}</span>
                                <span className="exercise-group">{exercise.group}</span>
                              </div>
                              {canEdit() && (
                                <div className="exercise-actions" onClick={(e) => e.stopPropagation()}>
                                  <button 
                                    className="icon-btn"
                                    onClick={() => setExerciseModal({ open: true, mode: 'edit', data: exercise })}
                                  >
                                    <Edit sx={{ fontSize: 14 }} />
                                  </button>
                                  <button 
                                    className="icon-btn danger"
                                    onClick={() => handleDeleteExercise(exercise)}
                                  >
                                    <Delete sx={{ fontSize: 14 }} />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>

                      {/* Seção: Disponíveis */}
                      <div className="exercises-section">
                        <div className="section-header available">
                          <CheckBoxOutlineBlank sx={{ fontSize: 18 }} />
                          <span>Disponíveis ({filteredUnassigned.length})</span>
                          {canEdit() && filteredUnassigned.length > 0 && (
                            <div className="select-all-btns">
                              <button 
                                className="select-btn"
                                onClick={selectAllToAssign}
                                title="Selecionar todos"
                              >
                                Todos
                              </button>
                              <button 
                                className="select-btn"
                                onClick={deselectAllToAssign}
                                title="Limpar seleção"
                              >
                                Nenhum
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {filteredUnassigned.length === 0 ? (
                          <div className="section-empty">Nenhum exercício disponível</div>
                        ) : (
                          filteredUnassigned.map(exercise => (
                            <div 
                              key={exercise.idactivitiesdivisionsexercises} 
                              className={`exercise-item available ${canEdit() && selectedToAssign.includes(exercise.idactivitiesdivisionsexercises) ? 'selected' : ''}`}
                              onClick={canEdit() ? () => toggleAssignSelection(exercise.idactivitiesdivisionsexercises) : undefined}
                            >
                              {canEdit() && (
                                <span className="checkbox-indicator">
                                  {selectedToAssign.includes(exercise.idactivitiesdivisionsexercises) 
                                    ? <CheckBox sx={{ fontSize: 20 }} /> 
                                    : <CheckBoxOutlineBlank sx={{ fontSize: 20 }} />
                                  }
                                </span>
                              )}
                              <div className="exercise-info">
                                <span className="exercise-name">{exercise.exercise}</span>
                                <span className="exercise-group">{exercise.group}</span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Floating Action Bar - Sempre visível quando há exercícios */}
                      {canEdit() && (filteredAssigned.length > 0 || filteredUnassigned.length > 0) && (
                        <div className="floating-actions">
                          <button 
                            className={`floating-btn unassign ${selectedToUnassign.length === 0 ? 'disabled' : ''}`}
                            onClick={handleBulkUnassign}
                            disabled={selectedToUnassign.length === 0}
                          >
                            <LinkOff sx={{ fontSize: 18 }} />
                            Desvincular ({selectedToUnassign.length})
                          </button>
                          <button 
                            className={`floating-btn copy ${selectedToUnassign.length === 0 ? 'disabled' : ''}`}
                            onClick={() => setCopyModal({ open: true, exerciseIds: selectedToUnassign })}
                            disabled={selectedToUnassign.length === 0}
                          >
                            <ContentCopy sx={{ fontSize: 18 }} />
                            Copiar
                          </button>
                          <button 
                            className={`floating-btn assign ${selectedToAssign.length === 0 ? 'disabled' : ''}`}
                            onClick={handleBulkAssign}
                            disabled={selectedToAssign.length === 0}
                          >
                            <LinkIcon sx={{ fontSize: 18 }} />
                            Vincular ({selectedToAssign.length})
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ==================== MODALS ==================== */}

      {/* Activity Modal */}
      {activityModal.open && (
        <ActivityModal
          mode={activityModal.mode}
          data={activityModal.data}
          loading={actionLoading}
          onClose={() => setActivityModal({ open: false, mode: 'create', data: null })}
          onSave={handleSaveActivity}
        />
      )}

      {/* Division Modal */}
      {divisionModal.open && (
        <DivisionModal
          mode={divisionModal.mode}
          data={divisionModal.data}
          loading={actionLoading}
          onClose={() => setDivisionModal({ open: false, mode: 'create', data: null })}
          onSave={handleSaveDivision}
        />
      )}

      {/* Exercise Modal */}
      {exerciseModal.open && (
        <ExerciseModal
          mode={exerciseModal.mode}
          data={exerciseModal.data}
          loading={actionLoading}
          onClose={() => setExerciseModal({ open: false, mode: 'create', data: null })}
          onSave={handleSaveExercise}
        />
      )}

      {/* Copy Modal */}
      {copyModal.open && (
        <CopyExercisesModal
          divisions={divisions.filter(d => d.idactivitiesdivisions !== selectedDivision?.idactivitiesdivisions)}
          exerciseCount={copyModal.exerciseIds.length}
          loading={actionLoading}
          onClose={() => setCopyModal({ open: false, exerciseIds: [] })}
          onCopy={handleBulkCopy}
        />
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        open={confirmModal.open}
        title="Confirmar Ação"
        message={confirmModal.message}
        loading={actionLoading}
        confirmLabel="Confirmar"
        onClose={() => setConfirmModal({ open: false, action: null, message: '', onConfirm: null })}
        onConfirm={confirmModal.onConfirm}
      />
    </div>
  );
};

// ==================== MODAL COMPONENTS ====================

const ActivityModal = ({ mode, data, loading, onClose, onSave }) => {
  const [form, setForm] = useState({
    activity: data?.activity || '',
    category: data?.category || '',
    description: data?.description || '',
    is_main_activity: data?.is_main_activity || false,
    has_divisions: data?.has_divisions ?? true,
    has_exercises: data?.has_exercises ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanData = {
      activity: form.activity.trim(),
      is_main_activity: form.is_main_activity,
      has_divisions: form.has_divisions,
      has_exercises: form.has_exercises
    };
    if (form.category && form.category.trim()) {
      cleanData.category = form.category.trim();
    }
    if (form.description && form.description.trim()) {
      cleanData.description = form.description.trim();
    }
    onSave(cleanData);
  };

  return (
    <FormModal
      open
      onClose={onClose}
      title={mode === 'create' ? 'Nova Atividade' : 'Editar Atividade'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" form="activity-form" loading={loading}>
            Salvar
          </Button>
        </>
      }
    >
      <form id="activity-form" onSubmit={handleSubmit}>
        <Input
          label="Nome da Atividade *"
          value={form.activity}
          onChange={(e) => setForm({ ...form, activity: e.target.value })}
          required
        />
        <Input
          label="Categoria"
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          placeholder="Ex: Força, Cardio, Flexibilidade"
        />
        <TextArea
          label="Descrição"
          rows={3}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <div className="form-checkboxes">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.is_main_activity}
              onChange={(e) => setForm({ ...form, is_main_activity: e.target.checked })}
            />
            <span>Atividade Principal</span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.has_divisions}
              onChange={(e) => setForm({ ...form, has_divisions: e.target.checked })}
            />
            <span>Possui Divisões</span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.has_exercises}
              onChange={(e) => setForm({ ...form, has_exercises: e.target.checked })}
            />
            <span>Possui Exercícios</span>
          </label>
        </div>
      </form>
    </FormModal>
  );
};

const DivisionModal = ({ mode, data, loading, onClose, onSave }) => {
  const [form, setForm] = useState({
    division: data?.division || '',
    description: data?.description || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanData = {
      division: form.division.trim()
    };
    if (form.description && form.description.trim()) {
      cleanData.description = form.description.trim();
    }
    onSave(cleanData);
  };

  return (
    <FormModal
      open
      onClose={onClose}
      title={mode === 'create' ? 'Nova Divisão' : 'Editar Divisão'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" form="division-form" loading={loading}>
            Salvar
          </Button>
        </>
      }
    >
      <form id="division-form" onSubmit={handleSubmit}>
        <Input
          label="Nome da Divisão *"
          value={form.division}
          onChange={(e) => setForm({ ...form, division: e.target.value })}
          placeholder="Ex: Superior, Inferior, Full Body"
          required
        />
        <TextArea
          label="Descrição"
          rows={3}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </form>
    </FormModal>
  );
};

const ExerciseModal = ({ mode, data, loading, onClose, onSave }) => {
  const [form, setForm] = useState({
    exercise: data?.exercise || '',
    group: data?.group || '',
    description: data?.description || '',
    has_load: data?.has_load || false,
    has_series: data?.has_series || false,
    has_rep: data?.has_rep || false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Limpar campos vazios
    const cleanData = {
      exercise: form.exercise.trim(),
      group: form.group,
      has_load: form.has_load,
      has_series: form.has_series,
      has_rep: form.has_rep
    };
    if (form.description && form.description.trim()) {
      cleanData.description = form.description.trim();
    }
    onSave(cleanData);
  };

  return (
    <FormModal
      open
      onClose={onClose}
      title={mode === 'create' ? 'Novo Exercício' : 'Editar Exercício'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" form="exercise-form" loading={loading}>
            Salvar
          </Button>
        </>
      }
    >
      <form id="exercise-form" onSubmit={handleSubmit}>
        <Input
          label="Nome do Exercício *"
          value={form.exercise}
          onChange={(e) => setForm({ ...form, exercise: e.target.value })}
          required
        />
        <Select
          label="Grupo Muscular *"
          value={form.group}
          onChange={(e) => setForm({ ...form, group: e.target.value })}
          required
        >
          <option value="">Selecione...</option>
          {MUSCLE_GROUPS.map((group) => (
            <option key={group} value={group}>
              {group}
            </option>
          ))}
        </Select>
        <TextArea
          label="Descrição"
          rows={3}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <div className="form-checkboxes">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.has_load}
              onChange={(e) => setForm({ ...form, has_load: e.target.checked })}
            />
            <Scale sx={{ fontSize: 16 }} />
            <span>Usa Carga</span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.has_series}
              onChange={(e) => setForm({ ...form, has_series: e.target.checked })}
            />
            <ViewList sx={{ fontSize: 16 }} />
            <span>Tem Séries</span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.has_rep}
              onChange={(e) => setForm({ ...form, has_rep: e.target.checked })}
            />
            <Repeat sx={{ fontSize: 16 }} />
            <span>Tem Repetições</span>
          </label>
        </div>
      </form>
    </FormModal>
  );
};

const CopyExercisesModal = ({ divisions, exerciseCount, loading, onClose, onCopy }) => {
  const [selectedDivision, setSelectedDivision] = useState('');

  return (
    <FormModal
      open
      onClose={onClose}
      title="Copiar Exercícios"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!selectedDivision}
            loading={loading}
            onClick={() => onCopy(parseInt(selectedDivision, 10))}
          >
            Copiar
          </Button>
        </>
      }
    >
      <p className="copy-info">
        Copiar <strong>{exerciseCount} exercício(s)</strong> para outra divisão:
      </p>
      <Select
        label="Divisão de destino *"
        value={selectedDivision}
        onChange={(e) => setSelectedDivision(e.target.value)}
        required
      >
        <option value="">Selecione...</option>
        {divisions.map((div) => (
          <option key={div.idactivitiesdivisions} value={div.idactivitiesdivisions}>
            {div.division}
          </option>
        ))}
      </Select>
    </FormModal>
  );
};

export default ActivitiesPage;
